<template>
  <div
    class="input"
    :style="{
      'flex-direction': labelStyle.flexDirection,
      'align-items': labelStyle.alignItems,
    }"
  >
    <div class="input__label-wrapper">
      <span
        v-if="label"
        :class="['input__label', { 'input__label-hover': titleHover }]"
        :style="{
          'margin-right': labelPosition === 'left' ? '15px' : null,
          'margin-left': labelPosition === 'right' ? '15px' : null,
          color: disabled ? '#AAAAAA' : labelColor,
        }"
        @click="titleClick()"
        >{{ label }}
      </span>
      <span v-if="required" class="input__required">
        <Icon value="required" width="10px" height="10px" />
      </span>

      <Icon
        v-if="hasDeleteButton"
        class="input__label-delete-btn"
        value="trash"
        width="14px"
        height="14px"
        @click="_handleDeleteButton"
      />
      <!-- <Icon
        v-if="required"
        class="input__required"
        color="#D19B3F"
        mode="fill"
        value="star"
      /> -->
    </div>
    <div
      :class="['input__inner', { input__inner_after_label: label }]"
      :style="{
        border: `1px solid ${
          !error ? (disabled ? '#BDBDBD' : innerBorder) : '#f44d4d'
        }`,
      }"
    >
      <span v-if="$slots.icon" class="input__icon"><slot name="icon" /></span>
      <slot></slot>
      <input
        v-if="pureInput"
        class="input__field"
        :disabled="disabled"
        :type="type"
        :placeholder="placeholder"
        :style="fieldStyle"
        :value="value"
        @input="onInput"
        @keydown.esc="_handleEscKeydown"
        @keydown.enter="_handleEnterKeydown"
        @focusout="onFocusOut"
      />
      <input
        v-else-if="lazy"
        v-model.lazy="inputValue"
        :maxlength="maxLength"
        :max="max"
        :min="min"
        class="input__field"
        :disabled="disabled"
        :type="type"
        :placeholder="placeholder"
        :style="fieldStyle"
        @keydown.esc="_handleEscKeydown"
        @keydown.enter="_handleEnterKeydown"
        @focusout="_handleFocusOut"
      />
      <input
        v-else
        v-model="inputValue"
        :maxlength="maxLength"
        :max="max"
        :min="min"
        class="input__field"
        :disabled="disabled"
        :type="type"
        :placeholder="placeholder"
        :style="fieldStyle"
        @keydown.esc="_handleEscKeydown"
        @keydown.enter="_handleEnterKeydown"
        @focusout="_handleFocusOut"
      />
    </div>
    <transition name="slide-y">
      <div v-if="error" class="input__error">{{ errorText }}</div>
    </transition>
  </div>
</template>

<script>
import { replaceSymbols } from '~packages/epk-front-shared';

import Icon from '../Icon';

export default {
  name: 'SingleLineInput',
  components: { Icon },
  props: {
    type: { default: '', type: String },
    disabled: { default: false, type: Boolean },
    fieldBorder: { default: '', type: String },
    fieldBackground: { default: '', type: String },
    format: { default: '', type: String }, // number
    innerBorder: { default: '#BDBDBD', type: String },
    label: { default: '', type: String },
    placeholder: { default: '', type: String },
    placeholderColor: { default: '#BDBDBD', type: String },
    required: { default: false, type: Boolean },
    hasDeleteButton: { default: false, type: Boolean },
    textAlign: { default: '', type: String },
    value: { default: '', type: [Number, String] },
    formatted: { default: false, type: Boolean },
    lazy: { default: false, type: Boolean },
    error: { default: false, type: Boolean },
    errorText: { default: '', type: String },
    trim: { default: false, type: Boolean },
    pureInput: { default: false, type: Boolean },
    labelPosition: {
      default: 'top',
      type: String,
      validator: function (value) {
        return ['top', 'right', 'bottom', 'left'].indexOf(value) !== -1;
      },
    },
    labelColor: {
      default: '',
      type: String,
    },
    maxLength: {
      default: null,
      type: Number,
    },
    maxFractional: {
      default: Infinity,
      type: Number,
    },
    titleHover: {
      default: false,
      type: Boolean,
    },
    max: {
      default: undefined,
      type: Number,
    },
    min: {
      default: undefined,
      type: Number,
    },
    needDelEndSymbols: {
      default: false,
      type: Boolean,
    },
  },
  emits: {
    input: null,
    onEnter: null,
    onEsc: null,
    focusout: null,
    delete: null,
  },
  data() {
    return {
      toggler: false,
    };
  },
  computed: {
    labelStyle() {
      const direction = {
        flexDirection: 'column',
        alignItems: 'flex-start',
      };
      switch (this.labelPosition) {
        case 'top':
          direction.flexDirection = 'column';
          break;
        case 'right':
          direction.flexDirection = 'row-reverse';
          direction.alignItems = 'center';
          break;
        case 'bottom':
          direction.flexDirection = 'column-reverse';
          break;
        case 'left':
          direction.flexDirection = 'row';
          direction.alignItems = 'center';
          break;
      }
      return direction;
    },

    fieldStyle() {
      const fieldStyle = {
        textAlign: this.textAlign,
        '--placeholder-color': this.placeholderColor,
      };
      const { fieldBorder, fieldBackground, $slots: slots, disabled } = this;

      if (!disabled) {
        if (fieldBorder && !fieldBackground && !disabled) {
          fieldStyle.border = fieldBorder;
        }

        if (fieldBackground) {
          fieldStyle.background = fieldBackground;
        }
      } else {
        fieldStyle.background = 'initial';
      }

      if (slots.icon) {
        fieldStyle.paddingLeft = `${36}px`;
      }

      if (disabled && this.innerBorder === '#BDBDBD') {
        fieldStyle.backgroundColor = '#F9F9F9';
      }

      return fieldStyle;
    },
    inputValue: {
      get() {
        const __to_watch__ = this.toggler;
        let _value = this.value;

        if (this.formatted && _value) {
          const valueNumber = Number(_value);
          _value = Number.isInteger(valueNumber)
            ? valueNumber.toLocaleString()
            : _value;
        }
        return _value;
      },
      set(value) {
        let _value = value;
        if (this.formatted) {
          if (_value) {
            const valueNumber = Number(value.replace(/\s/g, ''));
            _value = Number.isInteger(valueNumber) ? valueNumber : value;
          } else _value = null;
        }
        if (this.format === 'number') {
          let needToggle = false;
          // не применяем новое значение:
          if (
            _value >= Number.MAX_SAFE_INTEGER ||
            _value[0] === '.' ||
            _value?.match(/\./g)?.length > 1 || // добавлена 2-я точка
            (this.min >= 0 && _value[0] === '-') || // ожидаем не меньше 0, но первый знак "Минус"
            (_value?.split('.')?.[1]?.length > this.maxFractional &&
              _value >= this.value) || // дробная часть достигла заданного предела, но это не удаление
            (_value !== '' &&
              _value !== '-' &&
              _value.slice(-1) !== '.' &&
              !/^-?\d+(\.\d+?)?$/.test(_value)) || // не прошло проверку регуляркой
            (_value !== '' &&
              this.maxFractional === 0 &&
              !/^-?\d+$/.test(_value))
          ) {
            this.toggler = !this.toggler;
            return;
          }

          _value = Number.isNaN(+_value) ? _value : +_value; // все проверки прошли => _value - число, которое можно конвертнуть в number без потерь

          if (typeof this.max === 'number' && _value > this.max) {
            _value = this.max;
            needToggle = true;
          }
          if (typeof this.min === 'number' && _value < this.min) {
            _value = this.min;
            needToggle = true;
          }
          needToggle && (this.toggler = !this.toggler);
        }
        this.$emit('input', this.normalizeValue(_value));
      },
    },
  },
  methods: {
    titleClick() {
      this.$emit('title-click');
    },
    _handleDeleteButton() {
      this.$emit('delete');
    },
    _handleEscKeydown(event) {
      event.target.blur();
      this.$emit('onEsc');
    },
    _handleEnterKeydown() {
      this.$emit('onEnter');
    },
    _handleFocusOut() {
      this.$emit('focusout');
    },
    onFocusOut(e) {
      let str = e.target.value.trimEnd();

      if (this.needDelEndSymbols)
        str = replaceSymbols(str, '([^a-zA-Zа-яёА-ЯЁ0-9]+$)', '');

      this.$emit('input', str);
      this.$emit('focusout');
    },
    onInput(e) {
      if (!this.pureInput) return;
      let inputValue = e.target.value;

      if (inputValue.startsWith(' ')) inputValue = inputValue.trim();

      this.$emit('input', this.normalizeValue(inputValue));
      this.$forceUpdate();
    },
    normalizeValue(value) {
      return replaceSymbols(value, '([^a-zA-Zа-яёА-ЯЁ0-9])\\1+', '$1');
    },
  },
};
</script>

<style lang="scss" scoped>
@import './SingleLineInput';
</style>
